import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { X, Trash2 } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useCanvasContext } from "./WorkflowCanvasContext";
import { useWorkflow } from "../../../context/WorkflowContext";
import { getNodeRegistryEntry } from "./nodeRegistry";
import TriggerWebhookSection from "./ndv/TriggerWebhookSection";
import TriggerScheduleSection from "./ndv/TriggerScheduleSection";
import TriggerEmailSection from "./ndv/TriggerEmailSection";
import TriggerWhatsappSection from "./ndv/TriggerWhatsappSection";
import TriggerAppSection from "./ndv/TriggerAppSection";
import TriggerTestDataSection from "./ndv/TriggerTestDataSection";

const SLIDE_VARIANTS = {
    hidden: { x: "100%", opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { type: "spring", damping: 28, stiffness: 300 } },
    exit: { x: "100%", opacity: 0, transition: { duration: 0.2 } },
};

export default function TriggerDetailOverlay() {
    const { selectedNodeId, setSelectedNodeId, nodes } = useCanvasContext();
    const { workflow, refetchWorkflow } = useWorkflow();
    const { call: deleteTriggerCall } = useBluelagoon({ method: "deleteWorkflowTrigger" });
    const [confirmDelete, setConfirmDelete] = useState(false);

    const node = useMemo(() => nodes.find((n) => n.id === selectedNodeId), [nodes, selectedNodeId]);
    const triggerId = selectedNodeId?.replace("trigger-", "");
    const trigger = useMemo(
        () => (workflow?.workflowTriggers || []).find((t) => t.id === triggerId),
        [workflow?.workflowTriggers, triggerId]
    );

    const close = useCallback(() => setSelectedNodeId(null), [setSelectedNodeId]);

    const handleDelete = useCallback(async () => {
        if (!confirmDelete) { setConfirmDelete(true); return; }
        try {
            await deleteTriggerCall({ workflowId: workflow?.id, triggerId: trigger.id });
            toast.success("Trigger deleted");
            setSelectedNodeId(null);
            refetchWorkflow();
        } catch {
            toast.error("Failed to delete trigger");
        }
        setConfirmDelete(false);
    }, [confirmDelete, deleteTriggerCall, workflow?.id, trigger?.id, setSelectedNodeId, refetchWorkflow]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                if (confirmDelete) { setConfirmDelete(false); return; }
                close();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [close, confirmDelete]);

    if (!node || !trigger) return null;

    const entry = getNodeRegistryEntry(node.data.icon);
    const Icon = entry.icon;

    return (
        <motion.div
            key={selectedNodeId}
            className="absolute top-0 right-0 h-full w-[420px] bg-white border-l border-gray-200 shadow-lg z-20 flex flex-col"
            variants={SLIDE_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="exit"
        >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: entry.color + "15" }}>
                    <Icon size={14} style={{ color: entry.color }} />
                </div>
                <span className="text-sm font-medium text-gray-900 flex-1 truncate">{node.data.title}</span>
                <button
                    onClick={handleDelete}
                    className={`p-1 rounded transition-colors ${confirmDelete ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-100"}`}
                    title={confirmDelete ? "Click again to confirm" : "Delete trigger"}
                >
                    <Trash2 size={16} className={confirmDelete ? "text-red-500" : "text-gray-400"} />
                </button>
                <button onClick={close} className="p-1 hover:bg-gray-100 rounded">
                    <X size={16} className="text-gray-500" />
                </button>
            </div>
            {confirmDelete && (
                <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
                    <span className="text-xs text-red-700">Delete this trigger?</span>
                    <div className="flex gap-1">
                        <button onClick={() => setConfirmDelete(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                        <button onClick={handleDelete} className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
                    </div>
                </div>
            )}
            <div className="flex-1 overflow-y-auto">
                <TriggerContent trigger={trigger} workflowId={workflow?.id} />
                <TriggerTestDataSection />
            </div>
        </motion.div>
    );
}

function TriggerContent({ trigger, workflowId }) {
    const triggerType = trigger.type;

    if (triggerType === "WEBHOOK" || triggerType === "API") {
        return <TriggerWebhookSection trigger={trigger} workflowId={workflowId} />;
    }
    if (triggerType === "TIME") {
        return <TriggerScheduleSection trigger={trigger} workflowId={workflowId} />;
    }
    if (triggerType === "EMAIL") {
        return <TriggerEmailSection trigger={trigger} workflowId={workflowId} />;
    }
    if (triggerType === "WHATSAPP") {
        return <TriggerWhatsappSection trigger={trigger} workflowId={workflowId} />;
    }
    if (triggerType === "APP") {
        return <TriggerAppSection trigger={trigger} workflowId={workflowId} />;
    }

    return (
        <div className="p-4 text-sm text-gray-500 text-center">
            Trigger type &quot;{triggerType}&quot; — no configuration available.
        </div>
    );
}
